/**
 * Pure helpers for the /billing/family-balance page (Plan 09).
 *
 * Kept framework-free so they can be unit-tested without a DOM. The
 * React components import these directly; tests in
 * `client/test/components/billing-helpers.test.ts` exercise the rules.
 */

import type { BillingLedgerEntry, LedgerEntryKind } from '@/api/billing-family'

/**
 * Format a signed cents amount as a localized currency string. Returns
 * '+$25.00' for positive (TOPUP / REFUND) and '-$3.50' for negative
 * (CHARGE / debit ADJUSTMENT). Currency code follows the account's
 * currency column (one currency per account in v1).
 */
export function formatLedgerAmount(amountCents: number, currency: string): string {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  })
  const formatted = formatter.format(Math.abs(amountCents) / 100)
  if (amountCents > 0) {
    return `+${formatted}`
  }
  if (amountCents < 0) {
    return `-${formatted}`
  }
  return formatted
}

/**
 * Format a cents value as a currency string for display in the balance
 * card. SIGNED: negative balances render as "-$81.11" (clawbacks can
 * legitimately push a wallet below zero). Currency code per the
 * account's currency column.
 */
export function formatCents(amountCents: number, currency: string): string {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  })
  return formatter.format(amountCents / 100)
}

/**
 * The school(s) and students a household wallet covers, as one line:
 * "St. Hugo of the Hills Catholic School — Avery, Isaac, Lincoln".
 *
 * Load-bearing rather than decoration (PROD-1313). A household's
 * `displayName` is derived from ADULT SURNAMES ALONE
 * (`packages/schema/src/managers/household-name.ts`), so a guardian on two
 * households is GUARANTEED two identically-named wallets — "Numan Household"
 * twice. This line is then the only thing on screen that tells them apart,
 * and guessing wrong is a real charge.
 *
 * Null when the household carries neither: no line beats a stray separator.
 */
export function householdCoverageLine(account: {
  schoolNames?: readonly string[]
  studentNames?: readonly string[]
}): string | null {
  const schools = (account.schoolNames ?? []).join(', ')
  const students = (account.studentNames ?? []).join(', ')
  if (schools.length > 0 && students.length > 0) {
    return `${schools} — ${students}`
  }
  return schools || students || null
}

/**
 * Filter-chip groups for the billing-history table. Groups keep the bar
 * scannable: "Refunds" covers in-app refund credits AND Stripe card-refund
 * clawbacks (+ their voids); "Disputes" covers chargeback holds/releases.
 */
export const LEDGER_KIND_GROUPS: Array<{ value: string; label: string; kinds: LedgerEntryKind[] }> = [
  { value: 'TOPUP', label: 'Funds added', kinds: ['TOPUP'] },
  { value: 'CHARGE', label: 'Orders', kinds: ['CHARGE'] },
  { value: 'REFUND', label: 'Refunds', kinds: ['REFUND', 'TOPUP_REVERSAL', 'TOPUP_REVERSAL_VOID', 'TOPUP_CLAWBACK'] },
  { value: 'DISPUTE', label: 'Disputes', kinds: ['DISPUTE_HOLD', 'DISPUTE_RELEASE'] },
  { value: 'ADJUSTMENT', label: 'Adjustments', kinds: ['ADJUSTMENT'] },
]

/** Expand selected chip values into the ledger kinds they cover. */
export function expandKindGroups(selectedGroups: Set<string>): Set<LedgerEntryKind> {
  const kinds = new Set<LedgerEntryKind>()
  for (const option of LEDGER_KIND_GROUPS) {
    if (selectedGroups.has(option.value)) {
      for (const kind of option.kinds) {
        kinds.add(kind)
      }
    }
  }
  return kinds
}

/**
 * Human-readable label for each ledger entry kind. Matches the UI
 * chip + description text — keep in sync with the filter chips.
 */
export function ledgerKindLabel(kind: LedgerEntryKind): string {
  switch (kind) {
    case 'TOPUP':
      return 'Funds added'
    case 'CHARGE':
      return 'Order'
    case 'REFUND':
      return 'Refund'
    case 'ADJUSTMENT':
      return 'Adjustment'
    case 'TOPUP_REVERSAL':
      return 'Refund to card'
    case 'TOPUP_REVERSAL_VOID':
      return 'Refund canceled'
    case 'DISPUTE_HOLD':
      return 'Dispute hold'
    case 'DISPUTE_RELEASE':
      return 'Dispute resolved'
    case 'TOPUP_CLAWBACK':
      return 'Transfer failed'
  }
}

/**
 * Color-keyed badge class for ledger entries. The badge in the table
 * shifts color by kind so TOPUP / REFUND read as credit (green) and
 * CHARGE reads as debit (gray); ADJUSTMENT is neutral.
 */
export function ledgerKindBadgeVariant(kind: LedgerEntryKind): 'default' | 'success' | 'destructive' | 'secondary' {
  switch (kind) {
    case 'TOPUP':
      return 'success'
    case 'REFUND':
      return 'success'
    case 'CHARGE':
      return 'secondary'
    case 'ADJUSTMENT':
      return 'default'
    case 'TOPUP_REVERSAL':
      return 'secondary'
    case 'TOPUP_REVERSAL_VOID':
      return 'success'
    case 'DISPUTE_HOLD':
      return 'destructive'
    case 'DISPUTE_RELEASE':
      return 'success'
    case 'TOPUP_CLAWBACK':
      return 'destructive'
  }
}

/**
 * Format a date string (ISO 8601) as "Mar 14, 2025 12:32 PM" — matches
 * the user-provided mockup.
 */
export function formatLedgerTimestamp(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) {
    return iso
  }
  const datePart = d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  const timePart = d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })
  return `${datePart} ${timePart}`
}

/**
 * Build a single CSV row from a ledger entry. Fields are quoted so
 * commas / newlines inside `description` don't break the row.
 */
export function ledgerEntryToCsvRow(entry: BillingLedgerEntry): string {
  const cells = [
    entry.id,
    entry.occurredAt,
    entry.kind,
    String(entry.amountCents),
    String(entry.balanceAfter),
    entry.currency,
    entry.orderBatchId ?? '',
    entry.stripeIntentId ?? '',
    entry.description ?? '',
  ]
  return cells.map(csvEscape).join(',')
}

function csvEscape(cell: string): string {
  if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
    return `"${cell.replace(/"/g, '""')}"`
  }
  return cell
}

/**
 * Serialize a full set of ledger entries to a CSV blob string. The
 * first row is the header; remaining rows are entries in display
 * order (callers should pre-sort).
 */
export function ledgerEntriesToCsv(entries: readonly BillingLedgerEntry[]): string {
  const header = [
    'id',
    'occurred_at',
    'kind',
    'amount_cents',
    'balance_after',
    'currency',
    'order_batch_id',
    'stripe_intent_id',
    'description',
  ].join(',')
  const body = entries.map(ledgerEntryToCsvRow)
  return [header, ...body].join('\n')
}

/**
 * Sublabel for the "Filtered data" option of the billing-history CSV
 * download menu (PROD-1135). The count is the number of active
 * type-filter CHIPS, not the number of ledger kinds they expand to —
 * one chip ("Refunds") covers four kinds.
 */
export function ledgerCsvFilterScopeLabel(filterCount: number): string {
  if (filterCount === 0) {
    return 'Filtered data (no filters applied)'
  }
  return `Filtered data (${filterCount} filter${filterCount === 1 ? '' : 's'} applied)`
}

/**
 * Filename for a billing-history CSV export. A filtered export carries
 * a `-filtered` marker so a chip-scoped file can never be mistaken for
 * the unfiltered one once it is off the page (PROD-1135). Neither name
 * promises the FULL history — both feeds cap at the loaded page.
 */
export function ledgerCsvFilename(stamp: string, scope: 'filtered' | 'all'): string {
  return scope === 'filtered' ? `ordo-balance-history-filtered-${stamp}.csv` : `ordo-balance-history-${stamp}.csv`
}

/**
 * Filter a list of ledger entries by a (possibly-empty) set of kinds.
 * Empty set = show everything.
 */
export function filterLedgerEntries(
  entries: readonly BillingLedgerEntry[],
  kinds: ReadonlySet<LedgerEntryKind>,
): BillingLedgerEntry[] {
  if (kinds.size === 0) {
    return entries.slice()
  }
  return entries.filter((e) => kinds.has(e.kind))
}

/**
 * Paginate an in-memory list of ledger entries at the given page
 * size. v1 paginates client-side; switching to server pagination is a
 * follow-up when the table grows beyond ~500 entries per account.
 */
export interface LedgerPage {
  entries: BillingLedgerEntry[]
  page: number
  pageSize: number
  totalPages: number
  totalEntries: number
}

export function paginateLedgerEntries(
  entries: readonly BillingLedgerEntry[],
  page: number,
  pageSize: number,
): LedgerPage {
  if (pageSize <= 0) {
    throw new Error('paginateLedgerEntries: pageSize must be > 0')
  }
  const totalEntries = entries.length
  const totalPages = Math.max(1, Math.ceil(totalEntries / pageSize))
  const safePage = Math.max(0, Math.min(page, totalPages - 1))
  const start = safePage * pageSize
  const end = start + pageSize
  return {
    entries: entries.slice(start, end),
    page: safePage,
    pageSize,
    totalPages,
    totalEntries,
  }
}

/**
 * Resolve the active HOUSEHOLD account from a `me.ownedAccounts`
 * array. Mirrors the same picker on /my-family — there's one active
 * HOUSEHOLD account per parent; if none exists, the page renders the
 * "no household yet" empty state.
 */
export function findActiveHousehold<T extends { kind: 'HOUSEHOLD' | 'ORGANIZATION'; isArchived: boolean }>(
  accounts: readonly T[],
): T | null {
  return accounts.find((a) => a.kind === 'HOUSEHOLD' && !a.isArchived) ?? null
}

/**
 * Default auto-reload values:
 *   - threshold = $10 (1000 cents)
 *   - reload    = $100 (from @ordo/constants/wallet)
 *
 * These are pre-populated on first open of the auto-reload card so
 * the parent doesn't have to think about it.
 */
export const DEFAULT_AUTO_RELOAD_THRESHOLD_CENTS = 1000
export { DEFAULT_AUTO_RELOAD_AMOUNT_CENTS } from '@ordo/constants/wallet'

/**
 * When auto-reload charges the card, in the parent's words. Two triggers
 * since PROD-1400: the balance drops below the threshold, OR an order the
 * balance cannot cover is declined (a $6.94 wallet above a $5 threshold used
 * to skip every reload while its $8.69 auto-order failed). One sentence, so
 * every surface that asks a parent to turn auto-reload on says the same thing.
 */
export const AUTO_RELOAD_TRIGGER_SENTENCE =
  "We'll add funds when the balance drops below your threshold, or when it can't cover an order."

// ---------------------------------------------------------------------------
// BALANCE auto-order requirement (Tasker 0e05d6de)
// ---------------------------------------------------------------------------
//
// Client mirrors of the server policy in
// `server/src/managers/balance-autoreload-policy.ts`. The server is the
// enforcer; these exist so the UI can show the requirement inline instead of
// letting the parent walk into a 400. Keep the two in step — the doc comments
// on the server side explain each rule's reasoning.

/**
 * Does this household pay for orders out of its stored balance? Only INVOICED
 * does not; a null mode fails closed (households are PREPAID by construction
 * and a null mode cannot debit at all).
 */
export function accountPaysFromBalance(account: { billingMode: 'PREPAID' | 'INVOICED' | null }): boolean {
  return account.billingMode !== 'INVOICED'
}

/**
 * Is auto-reload actually able to fire? Enabled alone is not enough — a zero
 * threshold/amount or a missing default card each mean it can never top up.
 */
export function isAutoReloadArmed(
  account: {
    autoReload: boolean
    autoReloadAmountCents: number
    autoReloadThresholdCents: number | null
    defaultPaymentMethodRef: string | null
  } | null,
): boolean {
  return Boolean(
    account &&
    account.autoReload &&
    account.autoReloadAmountCents > 0 &&
    account.autoReloadThresholdCents !== null &&
    account.autoReloadThresholdCents > 0 &&
    account.defaultPaymentMethodRef,
  )
}

/**
 * Would the server refuse to create / edit / resume a balance-funded
 * auto-order on this household right now? Null account = still loading, which
 * is not a block (the caller renders a skeleton).
 *
 * `coverage` mirrors the server's free-meal waiver
 * (`balanceSubscriptionBlockReason` + `subscription-subsidy-coverage.ts`): a
 * subscription fully covered by the student's FREE meal plan never charges
 * the wallet, so no card is required. Null/undefined coverage (not fetched,
 * still loading) keeps the strict behavior — fail closed, same as the server.
 */
export function balanceAutoOrderBlocked(
  account:
    | ({ billingMode: 'PREPAID' | 'INVOICED' | null } & {
        autoReload: boolean
        autoReloadAmountCents: number
        autoReloadThresholdCents: number | null
        defaultPaymentMethodRef: string | null
      })
    | null,
  coverage?: { fullyCovered: boolean } | null,
): boolean {
  if (!account) {
    return false
  }
  if (coverage?.fullyCovered) {
    return false
  }
  return accountPaysFromBalance(account) && !isAutoReloadArmed(account)
}

/**
 * Plan 44 — parse a REFUND ledger entry's `description` to recover the
 * refund cause + proposal id. Mirrors the server-side encoder; pure
 * for unit tests. Returns null when the description isn't refund-
 * shaped (e.g., legacy REFUND rows written before Plan 44 shipped).
 *
 * Format: `[Refund:<CAUSE>][Proposal:<id>] <note>`
 */
export function parseRefundDescription(description: string | null | undefined): {
  cause: string
  proposalId: string
  note: string
} | null {
  if (!description) {
    return null
  }
  const m = /^\[Refund:([A-Z_]+)\]\[Proposal:([A-Za-z0-9_]+)\](?:\s+(.*))?$/.exec(description)
  if (!m) {
    return null
  }
  return {
    cause: m[1] ?? '',
    proposalId: m[2] ?? '',
    note: m[3] ?? '',
  }
}

/**
 * Trigger a browser download of a CSV string. Returns the blob URL
 * so callers can revoke it; the URL is auto-revoked after 60s as a
 * safety net.
 */
export function downloadCsv(filename: string, csv: string): string {
  if (typeof window === 'undefined') {
    throw new Error('downloadCsv: called outside the browser')
  }
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
  return url
}

/**
 * Validate the test-card fields of the local Add-payment-method dialog.
 *
 * Extracted so step 1 of that dialog can validate on "Next →" WITHOUT saving —
 * the same shape as the Stripe path, where `elements.submit()` surfaces field
 * errors before anything is charged. Pure, so the rules are unit-testable and
 * can't drift between the Next button and the final submit.
 *
 * `now` is injected rather than read from the clock so the expiry boundaries are
 * testable. Mirrors the server checks in `POST /api/billing/payment-methods/test`.
 */
export type TestCardDraft = { last4: string; expMonth: string; expYear: string }
export type TestCardValidation =
  | { ok: true; last4: string; expMonth: number; expYear: number }
  | { ok: false; message: string }

export function validateTestCard(draft: TestCardDraft, now: Date): TestCardValidation {
  const last4 = draft.last4.replace(/\D/g, '')
  const expMonth = Number(draft.expMonth)
  const expYear = Number(draft.expYear)
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth() + 1

  if (!/^\d{4}$/.test(last4)) {
    return { ok: false, message: 'Enter the last 4 digits for the card.' }
  }
  if (!Number.isInteger(expMonth) || expMonth < 1 || expMonth > 12) {
    return { ok: false, message: 'Enter a valid expiration month.' }
  }
  if (
    !Number.isInteger(expYear) ||
    expYear < currentYear ||
    expYear > currentYear + 25 ||
    (expYear === currentYear && expMonth < currentMonth)
  ) {
    return { ok: false, message: 'Enter a valid future expiration year.' }
  }
  return { ok: true, last4, expMonth, expYear }
}

/**
 * "Visa ending 4242" / "Apple Pay ending 4242" for the pay-with picker and
 * the top-up confirm step. (Lives here rather than in `top-up-sheet.tsx` so
 * `resolveTopUpConfirmStep` below — and its tests — can reach it.)
 */
export function describeCard(method: {
  walletType: string | null
  brand: string | null
  type: string
  last4: string | null
}): string {
  const wallet =
    method.walletType === 'apple_pay' ? 'Apple Pay' : method.walletType === 'google_pay' ? 'Google Pay' : null
  const name = wallet ?? method.brand ?? method.type ?? 'Card'
  const titled = name.charAt(0).toUpperCase() + name.slice(1)
  return method.last4 ? `${titled} ending ${method.last4}` : titled
}

export interface TopUpConfirmStep {
  /**
   * Non-null ⇒ CONFIRM-ONLY: the PaymentIntent already carries this
   * server-authorized card, so the step just names it and confirms. Null ⇒
   * mount the Payment Element to collect a card.
   */
  savedCardLabel: string | null
  /** Handed to the Payment Element. Always null on the saved-card path. */
  customerSessionClientSecret: string | null
}

/**
 * What the top-up confirm step must render for a given "Pay with" choice.
 *
 * Picking a saved card sends `paymentMethodId`, which the server authorizes
 * (`assertCanChargePaymentMethod`) and attaches to the PaymentIntent — so
 * there is nothing left to collect, and mounting a Payment Element just asks
 * the payer to retype the card she already picked (PROD-1003).
 *
 * `customerSessionClientSecret` stays null on that path ON PURPOSE: a
 * household shares ONE Stripe customer, so letting the Element redisplay its
 * saved cards would expose another guardian's PRIVATE card. Ordo's own picker
 * is the privacy boundary, not Stripe's.
 */
export function resolveTopUpConfirmStep(args: {
  /** The picker's choice: a `pm_…` id, or null for "use a new card". */
  chosenPaymentMethodId: string | null
  /** Cards the viewer may charge (already filtered server-side). */
  cards: readonly {
    stripePaymentMethodId: string
    type: string
    walletType: string | null
    brand: string | null
    last4: string | null
  }[]
  /** What the top-up intent returned. */
  customerSessionClientSecret: string | null | undefined
}): TopUpConfirmStep {
  if (!args.chosenPaymentMethodId) {
    return { savedCardLabel: null, customerSessionClientSecret: args.customerSessionClientSecret ?? null }
  }
  const chosen = args.cards.find((card) => card.stripePaymentMethodId === args.chosenPaymentMethodId)
  // An id the picker no longer lists is still confirm-only: the intent holds
  // the attached card whether or not the list caught up.
  return { savedCardLabel: chosen ? describeCard(chosen) : 'Your saved card', customerSessionClientSecret: null }
}
